import { formatCurrency } from "@/common/utils/formatters";
import { CreditProductCategoryConfig } from "./creditProductCategoryConfig";
import Accordion from "@/common/components/Accordion";
import TextField from "@/common/components/TextField";
import Checkbox from "@/common/components/Checkbox";
import { CompanyBranchData } from "../../types/companyBranches.type";
import { CompanyData } from "../../types/companies.type";
import { useParams } from 'react-router-dom';
import { useClientCompaniesAvailableToAllocateInBranchesSummary } from '../../hooks/useClientCompaniesAvailableToAllocateInBranchesSummary';
import { ClientCompanyAllowedCategoriesAndProducts } from '../../types/allowedCategoriesAndProducts.type';
import { ControlledByType } from "../../types/companyGovernmentAllocation.type";
import { ContractProductCategoryData } from "@/features/publicCompanyLeadsFunnel/types/publicCompanyLead";

interface CreditConfigurationProps {
  branchData?: CompanyBranchData
  companyData?: CompanyData
  controlledBy?: ControlledByType
  contractProductCategories?: ContractProductCategoryData[]
}

export const CreditConfiguration = ({
  branchData,
  companyData,
  controlledBy,
  contractProductCategories,
}: CreditConfigurationProps) => {
  const { companyId } = useParams();

  const {
    data: allowedCategoriesAndProducts,
  } = useClientCompaniesAvailableToAllocateInBranchesSummary<ClientCompanyAllowedCategoriesAndProducts>({
    companyId,
  });

  return (
    <div className="flex flex-col gap-4">
      <p className="font-bold text-lg">
        {companyData?.kind === 'public_company' ? 'Configurações de Cota' : 'Configurações de crédito'}
      </p>

      <div className="flex flex-col gap-2">
        <TextField
          data-testid='granted_credit_input'
          label={companyData?.kind === 'public_company' ? 'Cota de empenho da filial' : 'Crédito garantido a filial'}
          value={formatCurrency(Number(branchData?.granted_credit))}
          disabled
        />
        <div className="flex items-center gap-4">
          <p className="text-[12px] font-light italic">
            {companyData?.kind === 'public_company' ? 'Cota Global do Contrato' : 'Crédito garantido a empresa'}:{' '}
            {formatCurrency(Number(companyData?.granted_credit))}
          </p>

          <p className="text-[12px] font-light italic">
            {companyData?.kind === 'public_company' ? 'Cota livre para alocação' : 'Crédito livre para alocação'}:{' '}
            {formatCurrency(Number(companyData?.unallocated_credit))}
          </p>

          {companyData?.kind === 'public_company' && (
            <p className="text-[12px] font-light italic">
              Crédito empenhado na filial:{' '}
              {formatCurrency(Number(branchData?.government_allocation_requested_credit || 0))}
            </p>
          )}

          {branchData?.consumed_credit && (
            <p className="text-[12px] font-light italic">
              Crédito consumido:{' '}
              {formatCurrency(Number(branchData?.consumed_credit))}
            </p>
          )}
        </div>
      </div>

      {branchData?.product_category_granted_credits?.length ? (
        <Accordion
          head={(
            <Checkbox
              label="Crédito garantido por serviço"
              checked={!!branchData?.product_category_granted_credits.length}
              name="productCategories"
              disabled
            />
          )}
          body={(
            <div className="flex flex-col gap-4">
              {branchData?.product_category_granted_credits?.map((category, index) => (
                <CreditProductCategoryConfig
                  key={index}
                  categoryIndex={index}
                  category={category?.product_category}
                  grantedCredit={category?.granted_credit}
                  grantedQuantity={category?.granted_quantity}
                  products={category?.product_granted_credits || []}
                  allowedCategoriesAndProductsSummary={allowedCategoriesAndProducts}
                  branchData={branchData}
                  companyKind={companyData?.kind}
                  controlledBy={controlledBy}
                  contractProductCategory={contractProductCategories?.find(
                    (contractProductCategory) => contractProductCategory.product_category?.id === category.product_category?.id,
                  )}
                />
              ))}
            </div>
          )}
        />
      ) : null}
    </div>
  );
};
